// Static "what's happening at this stage" copy for the Baby Stage view.
// Keyed by locale, then by TCB phase (coarser than the week bands —
// developmental milestones don't change week to week). `whatsHappening` is the
// surprising/delightful stuff; `focusNow` is the parenting guidance.
//
// The Stage view shows this instantly + offline; the "Ask the coach" button
// layers a personalized Gemini answer on top.

import type { Phase } from "./tcbSchedule";
import type { Locale } from "../lib/i18n";

export type StageCopy = {
  /** Warm one-liner naming the stage. */
  headline: string;
  /** Surprising / interesting things developing right now. */
  whatsHappening: string[];
  /** What the caretaker should focus on — and not stress about — right now. */
  focusNow: string;
};

const enCopy: Record<Phase, StageCopy> = {
  "supply-building": {
    headline: "Brand new, finding her bearings",
    whatsHappening: [
      "She focuses best at about 8–12 inches — almost exactly the distance to your face during a feed.",
      "She already knows your voice and smell from before birth, and prefers them to anyone else's.",
      "Her world is mostly black, white and grey right now — high-contrast patterns catch her eye long before soft colors do.",
    ],
    focusNow:
      "Don't chase a schedule yet. Feed on her cues — and at least every 2–3 hours in the day, gently waking her if needed — do skin-to-skin, and rest when she rests. Supply and recovery, nothing else.",
  },
  foundation: {
    headline: "Waking up to the world",
    whatsHappening: [
      "Alert windows are stretching — she'll start studying faces instead of just feeding and dozing.",
      "She's beginning to track a slow-moving object with her eyes.",
      "A first real smile — the social kind, not a reflex — is usually only weeks away.",
    ],
    focusNow:
      "Ease into a gentle Eat → Awake → Sleep rhythm, but keep it loose. Aim for full feedings over snacks. Still no clock-watching.",
  },
  stretching: {
    headline: "Smiles, coos and longer nights",
    whatsHappening: [
      "Social smiles and the first coos arrive — she's learning that her sounds get a response.",
      "She's discovering her own hands exist, and will study them like tiny puzzles.",
      "Color vision is switching on — reds and greens come in first.",
    ],
    focusNow:
      "This is the window to gently stretch her first long night feed. When she stirs at night, pause and give her a few minutes to resettle before offering a feed — she may drift back on her own. Days can still flex around her.",
  },
  "regression-prep": {
    headline: "Sleep is reorganizing — and that's normal",
    whatsHappening: [
      "Her sleep cycles are maturing to look more like an adult's. The famous 4-month regression is really a permanent upgrade.",
      "She can track objects clear across the room and may turn toward sounds.",
      "Belly laughs and squeals tend to show up around now.",
    ],
    focusNow:
      "Expect a few rougher weeks — it passes. Protect her wake windows, cap daytime sleep around 5 hours, and keep bedtime steady.",
  },
  "regression-active": {
    headline: "Hands, rolls and one night feed",
    whatsHappening: [
      "She's grabbing everything in reach — and routing most of it straight to her mouth.",
      "Depth perception is coming online; a first roll can surprise you any day.",
      "She recognizes familiar faces across a room and lights up for them.",
    ],
    focusNow:
      "Aim to settle on one night feed by the end of month 4 — use her recent longest stretch as the bar before offering one. The swaddle is on its way out.",
  },
  "pre-sleep-training": {
    headline: "Bigger, more social, more opinionated",
    whatsHappening: [
      "Babbling is turning conversational — she'll pause for you to 'reply'.",
      "She may sit with support, and is working hard toward doing it solo.",
      "Stranger awareness is beginning — she's sorting the world into 'my people' and everyone else.",
    ],
    focusNow:
      "If nights still aren't where you want them, this is the age to work on independent sleep in earnest — a consistent bedtime, a steady wake time, and a calm routine. Everything you've built so far is the foundation it stands on.",
  },
};

const zhCopy: Record<Phase, StageCopy> = {
  "supply-building": {
    headline: "刚来到世界，慢慢熟悉一切",
    whatsHappening: [
      "她在大约 20–30 厘米处看得最清楚——差不多正好是喂奶时你脸的距离。",
      "她在出生前就已经熟悉你的声音和气味，而且比谁的都更喜欢。",
      "她现在的世界主要是黑、白、灰——高对比度的图案远比柔和的颜色更吸引她。",
    ],
    focusNow:
      "现在还不用追求作息表。按她的需求喂奶——白天至少每 2–3 小时一次，必要时温柔地把她唤醒——多做肌肤接触，她休息时你也休息。眼下只有两件事：建立奶量和恢复身体。",
  },
  foundation: {
    headline: "开始对世界睁开眼睛",
    whatsHappening: [
      "清醒的时间在变长——她会开始端详人脸，而不只是喝奶和打盹。",
      "她开始能用眼睛追着缓慢移动的物体看。",
      "第一个真正的微笑——是社交性的，不是反射——通常再过几周就会出现。",
    ],
    focusNow:
      "可以慢慢引入温和的「吃 → 玩 → 睡」节奏，但保持宽松。尽量一次喂饱，而不是少量多次地小口喂。还是不用盯着钟表。",
  },
  stretching: {
    headline: "微笑、咿呀声，还有更长的夜晚",
    whatsHappening: [
      "社交性微笑和最初的咿呀声出现了——她在学着发现自己的声音能换来回应。",
      "她发现了自己有一双小手，会像研究小谜题一样端详它们。",
      "色觉正在开启——最先看到的是红色和绿色。",
    ],
    focusNow:
      "这是温和地拉长她第一段夜间长睡的时机。她夜里翻动时，先停一停，给她几分钟自己重新安定，再决定是否喂奶——她可能会自己睡回去。白天还是可以顺着她来。",
  },
  "regression-prep": {
    headline: "睡眠正在重新组织——这很正常",
    whatsHappening: [
      "她的睡眠周期正在成熟，变得更像成人。著名的「4 个月睡眠倒退」其实是一次永久性的升级。",
      "她能追看房间另一头的物体，也会朝声音转头。",
      "咯咯大笑和兴奋的尖叫通常会在这时出现。",
    ],
    focusNow:
      "接下来几周可能会比较辛苦——会过去的。守好她的清醒时长，把白天睡眠控制在 5 小时左右，保持就寝时间稳定。",
  },
  "regression-active": {
    headline: "小手、翻身，还有一次夜奶",
    whatsHappening: [
      "她会去抓够得着的一切——而且大多都会直接塞进嘴里。",
      "深度知觉正在形成；某一天她可能会突然第一次翻身，给你一个惊喜。",
      "她能在房间另一头认出熟悉的面孔，并为之眼睛一亮。",
    ],
    focusNow:
      "争取在第 4 个月结束时稳定到一次夜奶——在喂之前，以她最近最长的一段连睡作为参考标准。包巾也快可以告别了。",
  },
  "pre-sleep-training": {
    headline: "更大了，更爱社交，也更有主意了",
    whatsHappening: [
      "咿呀声开始像对话——她会停下来等你「回应」。",
      "她可能能在支撑下坐着，并在努力练习自己独坐。",
      "认生开始了——她在把世界分成「我的人」和其他所有人。",
    ],
    focusNow:
      "如果夜间睡眠还不理想，这个月龄就可以认真培养自主入睡了——固定的就寝时间、稳定的起床时间，以及平静的睡前流程。你之前建立的一切，都是它的基础。",
  },
};

export const STAGE_COPY: Record<Locale, Record<Phase, StageCopy>> = {
  en: enCopy,
  zh: zhCopy,
};
